// src/views/Settings/org/OrgSettingsSection.jsx
//
// Los ajustes de la organización: sus datos y la zona de peligro. Estaban en un
// modal que abría otro modal —"Edit Details" lanzaba un diálogo encima de una
// pantalla completa—; aquí el formulario está en la sección, sin capas.
import React, { useState } from "react";
import GlassPanel from "../../../components/ui/GlassPanel";
import { Button } from "../../../components/ui/button";
import { XCircle } from "lucide-react";
import { useOrganizations } from "../../../hooks/useOrganizations";
import { resolveAccountDomainError, messageForResult } from "../../../services/accountDomainErrorMessages";
import { EditOrganizationForm } from "../../Organizations/OrganizationsView";

export default function OrgSettingsSection({ organization, role, onDeleted, onUpdated }) {
  const { updateOrganization, deleteOrganization } = useOrganizations();
  const [formError, setFormError] = useState(null);
  const [saved, setSaved] = useState(false);

  if (!organization) return null;

  const handleSubmit = async (formData) => {
    // Los campos vacíos no se mandan: un string vacío borraría el valor guardado
    // en vez de dejarlo como está.
    const cleaned = Object.entries(formData).reduce((acc, [key, value]) => {
      if (value && value.toString().trim() !== '') acc[key] = value;
      return acc;
    }, {});

    const result = await updateOrganization(organization.id, cleaned);
    if (result.success) {
      setFormError(null);
      setSaved(true);
      // Refetch en vez de `window.location.reload()`, que era lo que hacía el
      // modal: recargar la página entera para ver un nombre nuevo pierde el
      // contexto y hace parpadear toda la app.
      onUpdated?.();
      return;
    }
    const mapped = resolveAccountDomainError({ code: result.code, context: result.context });
    const statusLabel =
      result.status === 422 ? 'Validation error' :
      result.status === 403 ? 'You do not have permission to update this organization' :
      result.status === 404 ? 'Organization not found' :
      'Failed to update organization';
    const detail = result.error?.join(', ');
    setFormError(mapped || (detail ? `${statusLabel}: ${detail}` : statusLabel));
  };

  const handleDeactivate = async () => {
    const result = await deleteOrganization(organization.id);
    if (result?.success !== false) {
      onDeleted?.();
      return;
    }
    // Antes el fallo era mudo: sin `onDeleted` la pantalla se quedaba igual y
    // el usuario no sabía si había pasado algo. Es la única acción destructiva
    // de la sección, y la que más razones tiene de que el backend la rechace.
    setFormError(messageForResult(result, 'Failed to close organization'));
  };

  return (
    <div className="space-y-8">
      <GlassPanel className="p-8">
        <h2 className="mb-1 text-xl font-bold text-white">Organization details</h2>
        <p className="mb-6 text-sm text-gray-400">
          Update your organization's information
        </p>
        <EditOrganizationForm
          organization={organization}
          onSubmit={handleSubmit}
          formError={formError}
        />
        {saved && (
          <p className="mt-4 text-sm text-green-400">Organization updated.</p>
        )}
      </GlassPanel>

      {/* Solo quien es dueño. Un admin administra la organización; disolverla es
          otra cosa. */}
      {role === 'owner' && (
        <GlassPanel className="p-8">
          <h3 className="mb-2 text-lg font-semibold text-red-400">Danger zone</h3>
          <p className="mb-4 text-sm text-gray-400">
            Deactivate this organization. This action can be reversed from settings.
          </p>
          <Button
            onClick={handleDeactivate}
            variant="outline"
            className="border-red-500 text-red-400 hover:bg-red-500/20"
          >
            <XCircle className="mr-2 h-4 w-4" />
            Deactivate organization
          </Button>
        </GlassPanel>
      )}
    </div>
  );
}
